import { useMemo } from 'react';

import { useEvent } from '@mint-lib/hooks';
import { useQuery } from '@mint-lib/query-client';
import { Platforms } from '@mint-sdk/api-bff';
import camelcase from 'camelcase';

import { useService } from './useService.js';

export type PlatformsResponse = Platforms.PlatformsResponse;
export type Platform = Platforms.Platform;

type IconByLabel = {
  [platformLabel: Platform['label']]: Platform;
};
/**
 * Custom hook to fetch and manage platform data.
 * @param {Object} [options] - Optional query parameters for the request.
 * @param {boolean} [options.excludeDeleted=false] - Whether to exclude deleted platforms from the response.
 * @param {boolean} [options.excludeNotIntegrated=false] - Whether to exclude not integrated platforms from the response.
 * @param {boolean} [options.enabled=true] - Whether the underlying query should run.
 * @returns {Object} - An object containing platforms data, mapping of platforms by label and invalidate function.
 */
export const usePlatforms = (options?: {
  excludeDeleted?: boolean;
  excludeNotIntegrated?: boolean;
  enabled?: boolean;
}) => {
  const {
    excludeDeleted = false,
    excludeNotIntegrated = false,
    enabled = true,
  } = options || {};
  const [queryClient] = useService({
    name: 'queryClient',
    suspense: true,
  });
  const { data: platforms } = useQuery(
    {
      ...Platforms.listQueryOptions({
        params: { excludeDeleted, excludeNotIntegrated },
      }),
      enabled,
    },
    queryClient,
  );

  const invalidate = useEvent(() => {
    queryClient.invalidateQueries(
      Platforms.listQueryOptions({
        params: { excludeDeleted, excludeNotIntegrated },
      }),
    );
  });

  const platformByLabel = useMemo(
    () =>
      (platforms ?? []).reduce(
        (acc: IconByLabel, item) => ({
          ...acc,
          [camelcase(item.label)]: item,
          // need for logs logos
          [item.platform]: item,
        }),
        {},
      ),
    [platforms],
  );

  return { platforms, platformByLabel, invalidate };
};
